import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { UploadCloud } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { API } from "@/lib/engagements";

export const ImportDialog = ({ open, onOpenChange, onImported }) => {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [replace, setReplace] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!file) return toast.error("Choose an .xlsx file first");
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      const res = await axios.post(`${API}/engagements/import?replace=${replace}`, fd);
      toast.success(`Imported ${res.data.imported} engagements`);
      setFile(null);
      onOpenChange(false);
      onImported();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="import-dialog" className="bg-white rounded-sm sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-extrabold">Import from Excel</DialogTitle>
          <DialogDescription className="text-xs text-zinc-500">
            Upload an .xlsx engagement status workbook.
          </DialogDescription>
        </DialogHeader>
        <p className="text-sm text-zinc-500">
          Upload your Engagement Status workbook. Columns are read in the same order as your sheet:
          No, Client, Year End, Account (PIC → Submit for Audit), Audit (PIC → Report Issued), Tax (PIC → Finalise AFP).
        </p>
        <button
          data-testid="import-dropzone"
          onClick={() => inputRef.current?.click()}
          className="mt-2 flex w-full flex-col items-center justify-center gap-2 border border-dashed border-zinc-300 bg-zinc-50 py-10 transition-colors hover:border-zinc-900 hover:bg-white"
        >
          <UploadCloud className="h-6 w-6 text-zinc-500" />
          <span className="text-sm font-medium">{file ? file.name : "Click to select .xlsx file"}</span>
        </button>
        <input
          ref={inputRef}
          type="file"
          accept=".xlsx"
          data-testid="import-file-input"
          className="hidden"
          onChange={(e) => setFile(e.target.files?.[0] || null)}
        />
        <label className="flex items-center gap-2 text-sm">
          <Checkbox
            data-testid="import-replace-checkbox"
            checked={replace}
            onCheckedChange={(v) => setReplace(Boolean(v))}
          />
          Replace all existing engagements
        </label>
        <div className="flex justify-end gap-3 pt-2">
          <Button variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            data-testid="import-submit-btn"
            onClick={submit}
            disabled={busy}
            className="rounded-sm bg-black text-white hover:bg-zinc-800 hover:text-white"
          >
            {busy ? "Importing…" : "Import"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
